import React, { useCallback, useEffect, useState } from 'react';
import { supabase, isDevelopmentMode } from '../utils/supabase/client';
import { IconShieldAlt } from '../components/ui/icons';

/**
 * 🔐 DOUBLE VÉRIFICATION DE L'ADMINISTRATEUR (code à 6 chiffres d'une application d'authentification)
 * L'administration confirme les paiements et valide les identités : un mot de passe volé ne doit pas suffire.
 * - Première fois : le compte associe une application (Google Authenticator, Microsoft Authenticator…) par QR code.
 * - Ensuite : le code est demandé une fois par session.
 * La base (is_admin()) et le serveur de paiement refusent toute action d'administration sans cette vérification
 * (database/migration-2026-10-double-verification.sql). En démonstration, aucune vérification.
 */

const codeInput = 'w-full text-center font-mono text-2xl tracking-[0.4em] px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500';

const Frame = ({ title, children }) => (
  <div className="max-w-md mx-auto my-12 px-4">
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <IconShieldAlt className="h-9 w-9 text-green-700" aria-hidden="true" />
      <h1 className="mt-3 text-2xl font-bold text-gray-900">{title}</h1>
      {children}
    </div>
  </div>
);

const AdminMfaGate = ({ children }) => {
  const [state, setState] = useState({ step: 'loading' }); // loading | ok | verify | enroll | error
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const check = useCallback(async () => {
    try {
      const { data: level, error: levelError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (levelError) throw levelError;
      if (level.currentLevel === 'aal2') { setState({ step: 'ok' }); return; }
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) throw listError;
      const verified = (factors.totp || []).find(f => f.status === 'verified');
      if (verified) { setState({ step: 'verify', factorId: verified.id }); return; }
      // Associations commencées mais jamais terminées : retirées avant d'en créer une nouvelle
      await Promise.all((factors.all || []).filter(f => f.status !== 'verified')
        .map(f => supabase.auth.mfa.unenroll({ factorId: f.id })));
      const { data: enrolled, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp', friendlyName: `Administrateur ${new Date().toISOString().slice(0, 10)}`
      });
      if (enrollError) throw enrollError;
      setState({ step: 'enroll', factorId: enrolled.id, qr: enrolled.totp.qr_code, secret: enrolled.totp.secret });
    } catch (checkError) {
      setState({ step: 'error', message: checkError.message || 'Vérification impossible pour le moment.' });
    }
  }, []);

  useEffect(() => {
    if (isDevelopmentMode) { setState({ step: 'ok' }); return; }
    check();
  }, [check]);

  const submit = async (event) => {
    event.preventDefault();
    const clean = code.replace(/\D/g, '');
    if (clean.length !== 6) { setError('Saisissez les 6 chiffres affichés par l\'application.'); return; }
    setBusy(true);
    setError('');
    const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({ factorId: state.factorId, code: clean });
    setBusy(false);
    if (verifyError) {
      setError('Code incorrect ou expiré : saisissez le code affiché en ce moment (il change toutes les 30 secondes).');
      return;
    }
    setCode('');
    setState({ step: 'ok' });
  };

  if (state.step === 'ok') return children;
  if (state.step === 'loading') return <p className="text-center text-gray-600 my-16">Vérification de sécurité…</p>;
  if (state.step === 'error') {
    return (
      <Frame title="Vérification impossible">
        <p className="mt-3 text-gray-700">{state.message}</p>
        <button type="button" onClick={check} className="mt-4 bg-green-600 text-white px-5 py-2 rounded-xl hover:bg-green-700">Réessayer</button>
      </Frame>
    );
  }

  const form = (
    <form onSubmit={submit} className="mt-5 space-y-3">
      <label htmlFor="mfa-code" className="block text-sm font-medium text-gray-700">Code à 6 chiffres</label>
      <input
        id="mfa-code" value={code} onChange={e => setCode(e.target.value)} inputMode="numeric" autoComplete="one-time-code"
        maxLength={7} placeholder="000000" className={codeInput} autoFocus
      />
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      <button type="submit" disabled={busy} className="w-full bg-green-600 text-white font-semibold py-3 rounded-xl hover:bg-green-700 disabled:opacity-50">
        {busy ? 'Vérification…' : 'Valider'}
      </button>
    </form>
  );

  if (state.step === 'verify') {
    return (
      <Frame title="Code de sécurité">
        <p className="mt-3 text-gray-700">Ouvrez votre application d'authentification et saisissez le code « ImpactInvest ».</p>
        {form}
        <p className="mt-4 text-xs text-gray-500">Téléphone perdu ? Retirez l'ancienne association dans Supabase (Authentication &gt; Users &gt; votre compte &gt; MFA), puis reconnectez-vous.</p>
      </Frame>
    );
  }

  return (
    <Frame title="Activer la double vérification">
      <p className="mt-3 text-gray-700">
        L'administration confirme les paiements et valide les identités : elle est protégée par un code qui change
        toutes les 30 secondes, en plus de votre mot de passe. À faire une seule fois :
      </p>
      <ol className="mt-4 space-y-2 text-sm text-gray-800 list-decimal list-inside">
        <li>Installez sur votre téléphone <strong>Google Authenticator</strong> ou <strong>Microsoft Authenticator</strong> (gratuits).</li>
        <li>Dans l'application, touchez <strong>+</strong> puis <strong>Scanner un code QR</strong>, et scannez ce code :</li>
      </ol>
      <img src={state.qr} alt="Code QR à scanner avec l'application d'authentification" className="mx-auto my-4 h-48 w-48 rounded-lg border border-gray-200 bg-white p-2" />
      <p className="text-xs text-gray-600 text-center">
        Scan impossible ? Saisissez cette clé dans l'application :<br />
        <code className="font-mono text-sm break-all">{state.secret}</code>
      </p>
      <p className="mt-4 text-sm text-gray-800">3. Saisissez le code à 6 chiffres que l'application affiche :</p>
      {form}
    </Frame>
  );
};

export default AdminMfaGate;
